import type {
  CannedMacroAction,
  CannedResponseKind,
} from '../dataProvider/types';

/**
 * Display helpers, the macro-action vocabulary, and the variable-substitution engine for
 * the canned-response library (D-canned-responses, the Phase 6 surface-2 library).
 */

export const CANNED_KIND_LABEL: Record<CannedResponseKind, string> = {
  response: 'Response',
  macro: 'Macro',
};

export const CANNED_KIND_COLOR: Record<
  CannedResponseKind,
  'default' | 'primary'
> = {
  response: 'default',
  macro: 'primary',
};

/**
 * The bundled-action options offered when authoring a MACRO. v1 macros bundle a ticket
 * STATUS TRANSITION (resolve or escalate), each paired with the capability the chokepoint
 * guards on. `assign` is excluded (it writes an assignee, not a status) and the value-
 * moving actions (credit, suspend) are never a macro side effect.
 */
export interface MacroActionOption {
  value: string;
  label: string;
  action: CannedMacroAction;
}

export const MACRO_ACTION_OPTIONS: readonly MacroActionOption[] = [
  {
    value: 'resolve',
    label: 'Resolve the ticket',
    action: { capability: 'tickets:resolve', toStatus: 'resolved' },
  },
  {
    value: 'escalate',
    label: 'Escalate the ticket',
    action: { capability: 'tickets:escalate', toStatus: 'escalated' },
  },
];

/** Short label for a macro's bundled action, shown on the run affordance. */
export const MACRO_ACTION_LABEL: Record<CannedMacroAction['toStatus'], string> =
  {
    resolved: 'Resolve',
    escalated: 'Escalate',
  };

/**
 * The BOUNDED substitution variable set (D-canned-responses): exactly the fields already
 * joined into the inbox row, so a template can only ever interpolate data that exists. The
 * `balance` is the MEMBER-FACING floored figure (a member reads this copy), never the
 * signed balance. Adding a variable here means adding the matching field to the render
 * context; anything that would need data the row does not carry is out of scope.
 */
export const TEMPLATE_VARIABLES = [
  'member_name',
  'member_email',
  'balance',
  'market',
  'ticket_subject',
  'trigger',
] as const;
export type TemplateVariable = (typeof TEMPLATE_VARIABLES)[number];

/** The values a template renders against, built from the current ticket's member. */
export type TemplateContext = Record<TemplateVariable, string | null>;

const KNOWN: ReadonlySet<string> = new Set(TEMPLATE_VARIABLES);

/**
 * Render a template body, substituting `{variable}` placeholders from the context. An
 * UNKNOWN variable renders `[unknown: name]` and a KNOWN-but-UNAVAILABLE one (e.g. a
 * `{trigger}` on a ticket with none) renders `[no name]`: a clear marker either way, never
 * a silent blank, so the agent sees exactly what did not substitute before copying out.
 */
export const renderTemplate = (body: string, ctx: TemplateContext): string =>
  body.replace(/\{(\w+)\}/g, (_match, name: string) => {
    if (!KNOWN.has(name)) return `[unknown: ${name}]`;
    const value = ctx[name as TemplateVariable];
    if (value === null || value === undefined || value === '') {
      return `[no ${name}]`;
    }
    return value;
  });
